import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LoggerFactory } from '../logger/logger-factory.service';
import { LoggerWrapper } from '../logger/logger-wrapper.service';
import { ActivitesProvider } from '../activites/activites';
import { IMultiTechNotifications } from '../notifications-handler/multi-tech-notification-handler';
import { LoaderService } from '../loading.service';
import { AlertController } from '@ionic/angular';
import { lastValueFrom } from 'rxjs';
import { OtResponse } from '../../../api/models/ot-response';

/**
 * Type de résultats de get ot
 */
export enum GetOtResult {
  /**
   * Ot ok
   */
  ok,
  /**
   * Erreur Ot déjà acquittée
   */
  acknowledgeError,
  /**
   * Erreur Ot refusé
   */
  rejectError,
  /**
   * Autre type d'erreur non qualifiée
   */
  otherError,
}

/**
 * Type de notification
 */
export enum MultiTechNotification {
  /**
   * Notification liée à une demande d'intervention (DI) urgente en cascade
   */
  DICascade,
  /**
   * Notification liée à une demande d'intervention (DI) urgente
   */
  DIEmergency,
  /**
   * Notification liée à la fin d'une étape d'un STI
   */
  STIEndStep,
  /**
   * Notification affectation DI à Unité Organisationelle
   */
  DIUo,
}

/**
 * Type de notification
 */
export enum NotificationTypeItem {
  /**
   * Notification liée à une demande d'intervention (DI) urgente
   */
  OT = 'OT',
  /**
   * Notification liée à la fin d'une étape d'un STI
   */
  STI = 'STI',
}

export enum EventNameNotification {
  /**
   * Notification liée à une demande d'intervention (DI) urgente attribué par CSC
   */
  DIEmergency = 'act_ot_attribution_by_csc',
  /**
   * Notification liée à une demande d'intervention (DI) déclenché par une cascade CSC
   */
  DICascade = 'act_ot_cascade',
  /**
   * Notification liée à la fin d'une étape d'un STI
   */
  STIEndStep = 'act_sti_xmin_avant_fin_etape',

  DIUo = 'act_ot_uo',
}

/**
 * Affichage des notifications sous forme d'alert
 */
@Injectable({ providedIn: 'root' })
export class NotificationsAlertProvider {
  private loaderService = inject(LoaderService);
  /**
   * Logger
   */
  private logger: LoggerWrapper;

  /**
   * Constructeur
   * @param alertController
   * @param translate
   * @param loggerFactory
   * @param activitesProvider
   */
  constructor(
    private alertController: AlertController,
    private translate: TranslateService,
    private loggerFactory: LoggerFactory,
    private activitesProvider: ActivitesProvider
  ) {
    this.logger = this.loggerFactory.getInstance(this);
  }

  /**
   * Affiche la popup de notification d'un OT urgent
   */
  async showPopupDIEmergency(notification: IMultiTechNotifications) {
    const irId = notification.id;

    const titleKey = 'DI_EMERGENCY.TITLE';
    const detailsKey = 'DI_EMERGENCY.DETAILS';
    const loadingKey = 'GLOBAL.LOADING';
    const returnKey = 'GLOBAL.RETOUR';

    const trad = await lastValueFrom(this.translate.get([titleKey, detailsKey, loadingKey, returnKey]));
    const alert: HTMLIonAlertElement = await this.alertController.create();
    alert.header = trad[titleKey];
    //
    return new Promise((resolve) => {
      alert.buttons = [
        {
          text: trad[returnKey],
          handler: () => {},
        },
        {
          text: trad[detailsKey],
          handler: () => {
            this.loaderService.present(trad[loadingKey]);
            //
            this.getOt(irId).then((getOtResult) => {
              this.loaderService.dismiss();
              //
              alert.dismiss().then(() => {});
              //
              resolve({
                getOtResult,
                irId,
                type: MultiTechNotification.DIEmergency,
                notificationTypeItem: notification.typeItem,
              });
            });
            // ne ferme pas la pop-up si on a une erreur générique, peut être qu'il s'agit d'un problème passager (réseau)
            return false;
          },
        },
      ];
      //
      alert.present();
    });
  }

  /**
   * Affiche la popup de notification d'un OT urgent
   */
  async showPopupDIUo(notification: IMultiTechNotifications) {
    const irId = notification.id;
    const titleKey = 'DI_UO.TITLE';
    const detailsKey = 'DI_UO.DETAILS';
    const loadingKey = 'GLOBAL.LOADING';
    const returnKey = 'GLOBAL.RETOUR';
    const trad = await this.translate.get([titleKey, detailsKey, loadingKey, returnKey]).toPromise();
    let alert: HTMLIonAlertElement = await this.alertController.create();
    alert.header = trad[titleKey];
    //
    return new Promise((resolve) => {
      alert.buttons = [
        {
          text: trad[returnKey],
          handler: () => {},
        },
        {
          text: trad[detailsKey],
          handler: () => {
            this.loaderService.present(trad[loadingKey]);
            this.getOt(irId).then((getOtResult) => {
              this.loaderService.dismiss();
              //
              alert.dismiss().then(() => {});
              //
              resolve({
                getOtResult,
                irId,
                type: MultiTechNotification.DIUo,
                notificationEventName: notification.eventName,
                notificationTypeItem: notification.typeItem,
              });
            });
            // ne ferme pas la pop-up si on a une erreur générique, peut être qu'il s'agit d'un problème passager (réseau)
            return false;
          },
        },
      ];
      //
      alert.present();
    });
  }

  /**
   * Affiche la popup de notification d'un OT urgent
   */
  async showPopupDICascade(notification: IMultiTechNotifications) {
    const irId = notification.id;
    const titleKey = 'DI_CASCADE.TITLE';
    const detailsKey = 'DI_CASCADE.DETAILS';
    const loadingKey = 'GLOBAL.LOADING';
    const returnKey = 'GLOBAL.RETOUR';
    const trad = await this.translate.get([titleKey, detailsKey, loadingKey, returnKey]).toPromise();
    //
    let alert: HTMLIonAlertElement = await this.alertController.create();
    alert.header = trad[titleKey];
    //
    return new Promise((resolve) => {
      alert.buttons = [
        {
          text: trad[returnKey],
          handler: () => {},
        },
        {
          text: trad[detailsKey],
          handler: () => {
            this.loaderService.present(trad[loadingKey]);
            //
            this.getOt(irId).then((getOtResult) => {
              this.loaderService.dismiss();
              //
              alert.dismiss().then(() => {});
              //
              resolve({
                getOtResult,
                irId,
                type: MultiTechNotification.DICascade,
                notificationEventName: notification.eventName,
                notificationEstAstreinte: notification.est_astreinte,
              });
            });
            // ne ferme pas la pop-up si on a une erreur générique, peut être qu'il s'agit d'un problème passager (réseau)
            return false;
          },
        },
      ];
      //
      alert.present();
    });
  }

  /**
   * Récupère l'OT et retourne dans quel état elle se trouve
   * @param irId
   */
  async getOt(irId: string): Promise<GetOtResult> {
    let result = GetOtResult.otherError;
    try {
      const ot: OtResponse = await this.activitesProvider.getOt(irId);
      if (ot) {
        // Vérifie si l'OT a déjà été acquitée
        if (ot.prises_en_compte && ot.prises_en_compte.length > 0) {
          // lastPeC = dernier prises_en_compte
          let lastPeC = ot.prises_en_compte[ot.prises_en_compte.length - 1];
          if (lastPeC.type_prise_en_compte == 'acquittement') result = GetOtResult.acknowledgeError;
          else result = GetOtResult.ok;
          // else if(lastPeC.type_prise_en_compte == 'refus' && lastPeC.id_intervenant == this.authProvider.getCurrentUser()['id'])
          //   result = GetOtResult.rejectError;
        } else {
          result = GetOtResult.ok;
        }
      } else {
        this.logger.warn(`No content ot ${irId}`);
      }
    } catch (error) {
      this.logger.error(error, 'getOt error');
    }
    return result;
  }

  /**
   * Affiche une pop-up indiquant que l'OT a déjà été acquittée par un intervenant
   */
  async presentAlertOtAcknowledgeBy(message) {
    const retourKey = 'GLOBAL.RETOUR';
    const trad = await this.translate.get([retourKey]).toPromise();
    let alert: HTMLIonAlertElement = await this.alertController.create();
    alert.header = message;
    alert.buttons = [
      {
        text: trad[retourKey],
      },
    ];
    alert.present();
  }

  /**
   * Affiche une pop-up indiquant que l'OT a déjà été acquittée ou refusé
   */
  async presentAlertOtAcknowledgeOrReject(trad_key) {
    const retourKey = 'GLOBAL.RETOUR';
    const trad = this.translate.get([trad_key, retourKey]).toPromise();
    let alert: HTMLIonAlertElement = await this.alertController.create();
    alert.header = trad[trad_key];
    alert.buttons = [
      {
        text: trad[retourKey],
      },
    ];
    alert.present();
  }

  /**
   * Affiche la pop-up de délai supplémentaire STI
   * @param notification notification envoyée par le back
   */
  async showPopUpEndSTI(notification: IMultiTechNotifications) {
    const idSti = notification.id;
    const detailsKey = 'END_STI.DETAILS';
    const yesKey = 'GLOBAL.YES';
    const noKey = 'GLOBAL.NO';
    const trad = await this.translate.get([detailsKey, yesKey, noKey]).toPromise();
    //
    let alert: HTMLIonAlertElement = await this.alertController.create();
    alert.header = trad[detailsKey];
    return new Promise((resolve, reject) => {
      alert.buttons = [
        {
          text: trad[noKey],
          role: 'cancel',
          handler: () => {
            reject();
          },
        },
        {
          text: trad[yesKey],
          handler: () => {
            //
            alert.dismiss().then(() => {});
            //
            resolve({
              idSti,
              type: MultiTechNotification.STIEndStep,
              notificationTypeItem: notification.typeItem,
              libelleOt: notification.libelleOt,
            });
          },
        },
      ];
      //
      alert.present();
    });
  }
}
